import React, { CSSProperties, useMemo } from 'react';
import { Avatar } from 'antd';
import { useSelector } from 'react-redux';
import { stringToColour } from '../util/index';
import { getUserMe } from '../apps/main/rootReducer';
import { Contact } from '../models/Contact';

import * as ConfigValues from '../util/configValues';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../theme/types';
import { useQuery } from '@tanstack/react-query';

const CLASS_PREFIX = 'prio-user-avatar';

const STYLES: { [key: string]: CSSProperties } = {
  tiny: {
    width: 18,
    height: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 8,
  },
  small: {
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
  },
  medium: {
    width: 35,
    height: 35,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 15,
  },
  large: {
    width: 50,
    height: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 20,
  },
  extraLarge: {
    width: 88,
    height: 88,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 36,
  },
};

type AvatarSize = 'tiny' | 'small' | 'medium' | 'large' | 'extraLarge';

interface UserAvatarProps {
  size?: AvatarSize;
  contact?: Contact;
  displayName?: string;
  backgroundColor?: string;
  icon?: IconName;
  addWhiteBorder?: boolean;
  showGreenCheckMark?: boolean;
  /**
   * Special Avatar to display number of Avatars not shown. Will be indicated by +X. Useful when having an avatar row but to many to fit all in. See addInternalContactDrawerUserConfigStep.tsx for usage.
   */
  numberOfInvisibleAvatars?: number;
  showMe?: boolean;
}

export const UserAvatar: React.FC<UserAvatarProps> = (props) => {
  //#region -------------------------------- Variables
  const {
    size = 'medium',
    contact,
    displayName,
    backgroundColor,
    icon,
    numberOfInvisibleAvatars = 0,
    addWhiteBorder = false,
    showGreenCheckMark = false,
    showMe,
  } = props;
  const userMe = useSelector(getUserMe);
  const theme = useTheme<PrioTheme>();

  const checkIconPositionLeft =
    (STYLES[size].width as number) - (STYLES[size].fontSize as number);

  const checkIconPositionTop = -((STYLES[size].fontSize as number) * 0.85);

  const contactId = useMemo(() => {
    if (displayName) {
      return null;
    }
    if (contact?.contactType === 'ExternalContact') {
      return null;
    }
    return contact?.contactId ?? userMe?.id;
  }, [contact, userMe, displayName]);

  const { data: blobUrl } = useQuery({
    queryKey: ['contactPicture', ...(contactId ? [contactId] : [])],
    queryFn: async () => {
      if (contactId) {
        const response = await fetch(
          ConfigValues.REACT_APP_API_HOST_NAME +
            `/api/contact/public/contact/${contactId}/profilePhoto`
        );
        if (!response.ok) {
          return null;
        }
        const blob = await response.blob();
        if (!blob || blob.size === 0) {
          return null;
        }
        return URL.createObjectURL(blob);
      }
      return null;
    },
    staleTime: 1000 * 60 * 60 * 24,
  });
  //#endregion

  //#region -------------------------------- Methods
  const getUserOrContactName = (contact: Contact, displayName: string) => {
    if (showMe) {
      return userMe?.displayName?.split(' ') ?? [' ', ' '];
    }
    if (contact) {
      return [contact.firstName ?? ' ', contact.lastName ?? ' '];
    }
    if (displayName) {
      return displayName.split(' ') ?? [' ', ' '];
    }
    return undefined;
  };

  const getUserOrContactEmail = (contact: Contact) => {
    if (showMe) {
      return userMe?.mail;
    }
    if (contact) {
      return contact?.eMail;
    }
    return undefined;
  };

  const getStyle = () => {
    switch (size) {
      case 'tiny':
        return STYLES.tiny;

      case 'small':
        return STYLES.small;

      case 'medium':
        return STYLES.medium;

      case 'large':
        return STYLES.large;

      case 'extraLarge':
        return STYLES.extraLarge;
    }
  };

  const getImgStyle = () => {
    const avatarSize = STYLES[size]?.width;
    return { width: avatarSize, height: avatarSize };
  };

  const picture = () => {
    const name = getUserOrContactName(contact, displayName);
    if (!name) {
      return null;
    }
    return (
      <div
        className={`${CLASS_PREFIX}-pictureContainer`}
        style={{ overflow: 'visible' }}
      >
        {blobUrl ? (
          <img
            className={`${CLASS_PREFIX}-avatarPicture`}
            style={getImgStyle()}
            src={blobUrl}
            alt=""
          />
        ) : (
          <>
            <div
              className={`${CLASS_PREFIX}-avatarPicture`}
              style={getImgStyle()}
            />
            <div
              className={`${CLASS_PREFIX}-pictureOverlay`}
              style={{
                backgroundColor: backgroundColor
                  ? backgroundColor
                  : stringToColour(getUserOrContactEmail(contact) ?? ''),
                overflow: 'visible',
              }}
            >
              {numberOfInvisibleAvatars > 0
                ? `+${numberOfInvisibleAvatars}`
                : `${name?.[0]?.[0] ?? ' '}${name?.[1]?.[0] ?? ' '}`}
            </div>
          </>
        )}
      </div>
    );
  };

  //#endregion

  return (
    <div
      style={
        addWhiteBorder
          ? {
              height: (getStyle()?.height as number) + 6,
              width: (getStyle()?.width as number) + 6,
              padding: 3,
              margin: -3,
              borderRadius: '50%',
              backgroundColor: theme.colors.application.background.default,
              zIndex: 1,
            }
          : {}
      }
    >
      <Avatar
        className={`${CLASS_PREFIX}-avatar`}
        style={{
          ...getStyle(),
          ...(icon && { backgroundColor: 'transparent' }),
          fontSize:
            numberOfInvisibleAvatars > 0
              ? `calc(${getStyle()?.fontSize} * 0.8)`
              : getStyle()?.fontSize,
          border: showGreenCheckMark
            ? `2px solid ${theme.colors.base.green.default}`
            : undefined,
        }}
      >
        {icon ? <FontAwesomeIcon icon={['fal', icon]} /> : picture()}
      </Avatar>
      <FontAwesomeIcon
        icon={['fas', 'check-circle']}
        style={{
          position: 'relative',
          top: `${checkIconPositionTop}px`,
          left: `${checkIconPositionLeft}px`,
          color: theme.colors.base.green.default,
          fontSize: `${STYLES[size].fontSize as number}px`,
          display: showGreenCheckMark ? 'block' : 'none',
          backgroundColor: theme.colors.application.background.default,
          borderRadius: '50%',
        }}
      />
    </div>
  );
};

export default UserAvatar;
